import React from 'react';
import {
  ArrowRight,
  Bell,
  Briefcase,
  CheckCircle2,
  LogOut,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';

const joinClasses = (...classes) => classes.filter(Boolean).join(' ');

const formatNotificationTime = (value) => {
  if (!value) return 'Recently updated';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Recently updated';
  return date.toLocaleString([], {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getRequestMeta = (kind) => {
  const normalizedKind = String(kind || '').toLowerCase();
  if (normalizedKind === 'test_case_approval') {
    return {
      label: 'Test-case approval',
      chipClass: 'header-status-amber',
      dotClass: 'header-kind-dot-amber',
    };
  }
  if (normalizedKind === 'brd_share_request') {
    return {
      label: 'Sharing request',
      chipClass: 'header-status-emerald',
      dotClass: 'header-kind-dot-emerald',
    };
  }
  return {
    label: 'BRD approval',
    chipClass: 'header-status-blue',
    dotClass: 'header-kind-dot-blue',
  };
};

const HeaderPopoverHeading = ({
  icon: Icon,
  toneClass = 'header-tone-blue',
  eyebrow,
  title,
  description,
  actions = null,
}) => (
  <div className="header-popover-heading">
    <div className="header-popover-heading-copy">
      <span className={joinClasses('header-popover-leading-icon', toneClass)} aria-hidden="true">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <p className="header-popover-eyebrow">{eyebrow}</p>
        <h2 className="header-popover-title">{title}</h2>
        {description && <p className="header-popover-description">{description}</p>}
      </div>
    </div>
    {actions && <div className="header-popover-heading-actions">{actions}</div>}
  </div>
);

export const WorkspaceSwitcherMenu = ({
  id,
  currentMode,
  currentModeConfig,
  items,
  onSelect,
}) => {
  const CurrentIcon = currentModeConfig.icon;
  return (
    <div
      id={id}
      className="header-popover header-workspace-popover"
      role="dialog"
      aria-label="Switch product area"
    >
      <HeaderPopoverHeading
        icon={CurrentIcon}
        toneClass={currentModeConfig.toneClass}
        eyebrow="Product area"
        title="Switch area"
        description={`Currently using ${currentModeConfig.label}`}
      />

      <div className="header-workspace-options">
        {items.map((item) => {
          const ItemIcon = item.icon;
          const isActive = item.mode === currentMode;
          return (
            <button
              key={item.mode}
              type="button"
              onClick={() => onSelect(item, isActive)}
              aria-current={isActive ? 'page' : undefined}
              aria-label={isActive ? `${item.label}, current area` : `Switch to ${item.label}`}
              className={joinClasses(
                'header-workspace-option',
                item.toneClass,
                isActive && 'is-active'
              )}
            >
              <span className="header-workspace-option-top">
                <span className="header-workspace-option-icon" aria-hidden="true">
                  <ItemIcon className="h-4 w-4" />
                </span>
                {isActive && (
                  <span className="header-current-badge">
                    <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
                    Current
                  </span>
                )}
              </span>
              <span className="header-workspace-option-copy">
                <strong>{item.label}</strong>
                <span>{item.description}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const ApplicationSwitcherMenu = ({
  id,
  applications = [],
  activeApplication = null,
  loading = false,
  onSelect,
  onOpenAll,
}) => (
  <div
    id={id}
    className="header-popover header-workspace-popover"
    role="dialog"
    aria-label="Switch application"
  >
    <HeaderPopoverHeading
      icon={Briefcase}
      toneClass="header-tone-emerald"
      eyebrow="Application"
      title="Switch application"
      description={
        activeApplication?.name
          ? `Currently testing ${activeApplication.name}`
          : 'Choose the product you want to test'
      }
    />

    <div className="header-application-options">
      {loading ? (
        <div className="header-feed-empty">
          <RefreshCw className="h-4 w-4 animate-spin" aria-hidden="true" />
          <div>
            <strong>Loading applications</strong>
            <span>Fetching products shared with you.</span>
          </div>
        </div>
      ) : applications.length ? (
        applications.map((application) => {
          const isActive = String(application?.id || '') === String(activeApplication?.id || '');
          return (
            <button
              key={application.id}
              type="button"
              className={joinClasses('header-application-option', isActive && 'is-active')}
              aria-current={isActive ? 'page' : undefined}
              onClick={() => onSelect(application, isActive)}
            >
              <span className="header-workspace-option-icon" aria-hidden="true">
                <Briefcase className="h-4 w-4" />
              </span>
              <span className="min-w-0">
                <strong>{application.name}</strong>
                <small>{application.current_user_role || 'member'} access</small>
              </span>
              {isActive
                ? <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                : <ArrowRight className="h-4 w-4" aria-hidden="true" />}
            </button>
          );
        })
      ) : (
        <div className="header-feed-empty">
          <Briefcase className="h-4 w-4" aria-hidden="true" />
          <div>
            <strong>No applications</strong>
            <span>Create an application to begin testing.</span>
          </div>
        </div>
      )}
    </div>

    <button type="button" className="header-popover-primary" onClick={onOpenAll}>
      All applications
      <ArrowRight className="h-4 w-4" aria-hidden="true" />
    </button>
  </div>
);

export const NotificationsMenu = ({
  id,
  totalNotificationCount,
  pendingCount,
  pendingBrd,
  pendingSharing,
  pendingTestCases,
  unreadUpdates,
  requestItems,
  updateItems,
  loading,
  onRefresh,
  onMarkRead,
  onOpenRequest,
  onOpenUpdate,
  onOpenRequestCenter,
}) => (
  <div
    id={id}
    className="header-popover header-notifications-popover"
    role="dialog"
    aria-label="Requests and updates"
    aria-busy={loading}
  >
    <HeaderPopoverHeading
      icon={Bell}
      toneClass="header-tone-amber"
      eyebrow="Inbox"
      title="Requests & updates"
      description={
        totalNotificationCount > 0
          ? `${totalNotificationCount} item${totalNotificationCount === 1 ? '' : 's'} need your attention`
          : 'Everything is up to date'
      }
      actions={(
        <>
          {unreadUpdates > 0 && (
            <button
              type="button"
              className="header-popover-action"
              onClick={onMarkRead}
              disabled={loading || typeof onMarkRead !== 'function'}
            >
              Mark read
            </button>
          )}
          <button
            type="button"
            className="header-popover-action header-popover-action-icon"
            onClick={onRefresh}
            disabled={loading || typeof onRefresh !== 'function'}
            aria-label="Refresh requests and updates"
            title="Refresh"
          >
            <RefreshCw className={joinClasses('h-4 w-4', loading && 'animate-spin')} />
          </button>
        </>
      )}
    />

    <div className="header-notifications-body">
      <div className="header-summary-grid" aria-label="Pending request summary">
        <div className="header-summary-stat header-summary-stat-blue">
          <span>BRD approvals</span>
          <strong>{pendingBrd}</strong>
        </div>
        <div className="header-summary-stat header-summary-stat-emerald">
          <span>Sharing</span>
          <strong>{pendingSharing}</strong>
        </div>
        <div className="header-summary-stat header-summary-stat-amber">
          <span>Test cases</span>
          <strong>{pendingTestCases}</strong>
        </div>
      </div>

      <section className="header-feed-section" aria-labelledby={`${id}-requests-title`}>
        <div className="header-feed-section-heading">
          <h3 id={`${id}-requests-title`}>Pending requests</h3>
          <span>{pendingCount}</span>
        </div>
        <div className="header-feed-list">
          {loading ? (
            <div className="header-feed-empty">
              <RefreshCw className="h-4 w-4 animate-spin" aria-hidden="true" />
              <div>
                <strong>Refreshing requests</strong>
                <span>Fetching the latest application activity.</span>
              </div>
            </div>
          ) : requestItems.length > 0 ? (
            requestItems.map((item, index) => {
              const kind = String(item?.kind || '').toLowerCase();
              const meta = getRequestMeta(kind);
              const itemTitle = String(
                item?.title
                || item?.section_title
                || item?.pdf_file
                || item?.pdf_id
                || 'Pending request'
              ).trim();
              const itemContext = kind === 'test_case_approval'
                ? `Section ${item?.section_number || '-'}`
                : String(item?.pdf_file || item?.pdf_id || 'Application').trim();
              const itemTime = formatNotificationTime(item?.updated_at || item?.requested_at || null);
              return (
                <button
                  key={`${kind}-${item?.pdf_id || 'pdf'}-${item?.request_id || item?.section_number || index}`}
                  type="button"
                  className="header-feed-item"
                  onClick={() => onOpenRequest(item)}
                >
                  <span className="header-feed-item-row">
                    <strong>{itemTitle}</strong>
                    <span className={joinClasses('header-status-chip', meta.chipClass)}>
                      {meta.label}
                    </span>
                  </span>
                  <span className="header-feed-item-meta">
                    <span className={joinClasses('header-kind-dot', meta.dotClass)} aria-hidden="true" />
                    <span className="truncate">{itemContext}</span>
                    <span aria-hidden="true">·</span>
                    <time>{itemTime}</time>
                  </span>
                </button>
              );
            })
          ) : (
            <div className="header-feed-empty header-feed-empty-success">
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
              <div>
                <strong>All clear</strong>
                <span>No pending requests right now.</span>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="header-feed-section" aria-labelledby={`${id}-updates-title`}>
        <div className="header-feed-section-heading">
          <h3 id={`${id}-updates-title`}>Recent updates</h3>
          {unreadUpdates > 0 && <span className="header-unread-badge">{unreadUpdates} unread</span>}
        </div>
        <div className="header-feed-list header-updates-list">
          {updateItems.length > 0 ? (
            updateItems.map((item) => {
              const isUnread = String(item?.status || '').toLowerCase() !== 'read';
              const itemTime = formatNotificationTime(item?.created_at || null);
              return (
                <button
                  key={item?.id || `${item?.kind}-${item?.created_at}`}
                  type="button"
                  className={joinClasses('header-feed-item', isUnread && 'is-unread')}
                  onClick={() => onOpenUpdate(item)}
                >
                  <span className="header-feed-item-row">
                    <strong>{item?.title || 'Approval update'}</strong>
                    {isUnread && <span className="header-new-badge">New</span>}
                  </span>
                  {item?.message && <span className="header-feed-message">{item.message}</span>}
                  <time className="header-feed-time">{itemTime}</time>
                </button>
              );
            })
          ) : (
            <div className="header-feed-empty">
              <Bell className="h-4 w-4" aria-hidden="true" />
              <div>
                <strong>No recent updates</strong>
                <span>Approval activity will appear here.</span>
              </div>
            </div>
          )}
        </div>
      </section>

      <button
        type="button"
        className="header-popover-primary"
        onClick={onOpenRequestCenter}
      >
        Open Request Center
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  </div>
);

export const ProfileMenu = ({
  id,
  userLabel,
  userEmail,
  userRoleLabel,
  userInitials,
  onSecurity,
  onLogout,
}) => (
  <div
    id={id}
    className="header-popover header-profile-popover"
    role="dialog"
    aria-label="Account options"
  >
    <div className="header-profile-summary">
      <span className="header-profile-avatar header-profile-avatar-large" aria-hidden="true">
        {userInitials}
      </span>
      <div className="min-w-0">
        <p className="header-popover-eyebrow">Signed in</p>
        <h2 className="header-profile-name">{userLabel}</h2>
        {userEmail && userEmail !== userLabel && (
          <p className="header-profile-email">{userEmail}</p>
        )}
        {userRoleLabel && <span className="header-profile-role">{userRoleLabel}</span>}
      </div>
    </div>

    <div className="header-profile-actions">
      <button type="button" className="header-profile-action" onClick={onSecurity}>
        <span className="header-profile-action-icon" aria-hidden="true">
          <ShieldCheck className="h-4 w-4" />
        </span>
        <span>
          <strong>Security settings</strong>
          <small>Manage MFA and account protection</small>
        </span>
      </button>

      <div className="header-profile-divider" />

      <button
        type="button"
        className="header-profile-action header-profile-action-danger"
        onClick={onLogout}
      >
        <span className="header-profile-action-icon" aria-hidden="true">
          <LogOut className="h-4 w-4" />
        </span>
        <span>
          <strong>Sign out</strong>
          <small>End your TestSuite AI session</small>
        </span>
      </button>
    </div>
  </div>
);
